/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import * as MsDyn365 from '@msdyn365-commerce/core';
import { IModuleStateProps, withModuleState } from '@msdyn365-commerce-modules/checkout-utilities';
import { Button, getTelemetryObject, IModuleProps, INodeProps, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import { CheckoutModule, ErrorLocation } from '@msdyn365-commerce/global-state';
import classname from 'classnames';
import { action, computed, observable, reaction, when } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

import { ILdsCheckoutTermsAndConditions4Data } from './lds-checkout-terms-and-conditions-4.data';
import { ILdsCheckoutTermsAndConditions4Props } from './lds-checkout-terms-and-conditions-4.props.autogenerated';
import CheckboxComponent from './components/lds-checkout-terms-and-conditions-4.check-box';
import ErrorMessageComponent from './components/lds-checkout-terms-and-conditions-4.error-message';
import ErrorTitleComponent from './components/lds-checkout-terms-and-conditions-4.error-title';
import { focusOnCheckoutError } from '@msdyn365-commerce-modules/checkout';
import { IChurchRetailChannelPolicyEntity } from '../../actions/DataServiceEntities.g';

export interface ICheckoutTermsAndConditionsModuleProps
    extends ILdsCheckoutTermsAndConditions4Props<ILdsCheckoutTermsAndConditions4Data>,
        IModuleStateProps {}

export interface ICheckoutTermsAndConditions {
    termsAndConditionAcceptance: INodeProps;
    checkBox: React.ReactNode;
    description: React.ReactNode;
}

export interface ICheckoutTermsAndConditionsError {
    error: INodeProps;
    title: React.ReactNode;
    message: React.ReactNode;
}

export interface ICheckoutTermsAndConditionsViewState {
    isError: boolean;
    isShowSaveButton: boolean;
    isShowEditButton: boolean;
    isShowCancelButton: boolean;
}

export interface ICheckoutTermsAndConditionsViewProps extends ILdsCheckoutTermsAndConditions4Props<{}> {
    checkoutTermsAndConditions: IModuleProps;
    isTermsAccepted: boolean;
    errorMessageData: string;
    viewState: ICheckoutTermsAndConditionsViewState;
    termsAndConditionsContent: ICheckoutTermsAndConditions;
    salesAndRefundPolicy?: IChurchRetailChannelPolicyEntity | undefined;
    errorMessage?: ICheckoutTermsAndConditionsError;
    saveButton?: React.ReactNode;
    editButton?: React.ReactNode;
    cancelButton?: React.ReactNode;
    checkoutErrorRef?: React.RefObject<HTMLElement>;
    onChange(event: React.ChangeEvent<HTMLInputElement>): void;
    onSave(): void;
    onEdit(): void;
    onCancel(): void;
}

/**
 *
 * CheckoutTermsAndConditions component.
 * @extends {React.Component<ILdsCheckoutTermsAndConditions4Props<ILdsCheckoutTermsAndConditions4Data>>}
 */
// @ts-expect-error
@withModuleState
@observer
class CheckoutTermsAndConditions extends React.Component<ICheckoutTermsAndConditionsModuleProps> {
    private readonly telemetryContent: ITelemetryContent;

    private readonly checkoutErrorRef: React.RefObject<HTMLElement> = React.createRef();

    @observable private isTermsAccepted: boolean = true;

    @observable private errorMessage?: string;

    constructor(props: ICheckoutTermsAndConditionsModuleProps) {
        super(props);
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
    }

    @computed get isDataReady(): boolean {
        return (this.props.data.checkout.result && this.props.data.checkout.status) === 'SUCCESS';
    }

    @computed get isShowButtons(): boolean {
        const { showControlButtons } = this.props.config;
        return !this.props.moduleState.hasExternalSubmitGroup && (showControlButtons || false);
    }

    @computed get isAutoSave(): boolean {
        const { showControlButtons } = this.props.config;
        return !this.props.moduleState.hasExternalSubmitGroup && !showControlButtons;
    }

    @computed get defaultTermsAndConditionAcceptedValue(): boolean {
        const { showCheckbox } = this.props.config;
        return !showCheckbox;
    }

    public async componentDidMount(): Promise<void> {
        when(
            () => this.isDataReady,
            async () => {
                await this.init();
            }
        );

        reaction(
            () => this.props.data.checkout.result && this.props.data.checkout.result.isTermsAndConditionAccepted,
            async () => {
                this.isTermsAccepted =
                    this.props.data.checkout.result?.isTermsAndConditionAccepted || this.defaultTermsAndConditionAcceptedValue;
            }
        );

        if (this.props.data.checkout.result?.shouldEnableCheckoutErrorDisplayMessaging) {
            reaction(
                () => this.props.data.checkout.result?.checkoutError,
                checkoutError => {
                    if (
                        checkoutError &&
                        checkoutError.errorLocation === ErrorLocation.CheckoutTermsAndConditions &&
                        checkoutError.errorMessage
                    ) {
                        this.setError(checkoutError.errorMessage);
                    }
                }
            );

            reaction(
                () => this.props.data.checkout.result?.checkoutErrorFocus,
                checkoutErrorFocus => {
                    if (checkoutErrorFocus === CheckoutModule.CheckoutTermsAndConditions) {
                        void focusOnCheckoutError(this.checkoutErrorRef, this.props.context.actionContext);
                    }
                }
            );
        }
    }

    public shouldComponentUpdate(
        nextProps: ICheckoutTermsAndConditionsModuleProps,
        nextState: ICheckoutTermsAndConditionsViewState
    ): boolean {
        if (this.state === nextState && this.props === nextProps) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element | null {
        const {
            moduleState: { isReady, hasError },
            config: { className }
        } = this.props;
        const isTermsAndConditionAccepted = this.props.data.checkout.result?.isTermsAndConditionAccepted;
        const isError = hasError;
        const isShowContent = !isReady;
        const isShowSelected = !hasError && isReady;

        const policies = this.props.data.policy.result;
        const salesAndRefundPolicy = policies?.find((policy: any) => policy.USEFORCHECKOUT === 1 || policy.UseForCheckout === 1);

        const viewProps = {
            ...this.props,
            ...this.state,
            salesAndRefundPolicy,
            isTermsAccepted: this.isTermsAccepted,
            errorMessageData: this.errorMessage,
            checkoutErrorRef: this.checkoutErrorRef,
            viewState: {
                isError,
                isShowSaveButton: isShowContent && this.isShowButtons,
                isShowEditButton: isShowSelected && this.isShowButtons,
                isShowCancelButton: isShowContent && this.isShowButtons && isTermsAndConditionAccepted
            },
            checkoutTermsAndConditions: {
                moduleProps: this.props,
                className: classname('ms-checkout-terms-and-conditions', className)
            },
            termsAndConditionsContent: this.getTermsAndConditions(),
            errorMessage: this.getErrorMessage(),
            saveButton: this.getSaveButton(),
            editButton: this.getEditButton(),
            cancelButton: this.getCancelButton(),
            onSave: this.onSubmit,
            onEdit: this.onEdit,
            onCancel: this.onCancel,
            onChange: this.onChange
        };
        return this.props.renderView(viewProps) as React.ReactElement;
    }

    public handleTextChange = (event: MsDyn365.ContentEditableEvent) => (this.props.config.description = event.target.value);

    private readonly getTermsAndConditions = (): ICheckoutTermsAndConditions => {
        const {
            moduleState: { isReady },
            config: { showCheckbox, description }
        } = this.props;
        const isDisabled = isReady && !this.isAutoSave;
        const isTermsAndConditionAccepted = this.isTermsAccepted;
        const { checkboxAriaLabel } = this.props.resources;
        return {
            termsAndConditionAcceptance: { className: 'ms-checkout-terms-and-conditions__container' },
            checkBox: showCheckbox && (
                <CheckboxComponent
                    {...{
                        isChecked: isTermsAndConditionAccepted,
                        ariaLabel: checkboxAriaLabel,
                        disabled: isDisabled,
                        onChange: this.onChange,
                        id: this.props.id,
                        telemetryContent: this.telemetryContent
                    }}
                />
            ),
            description: (
                <MsDyn365.RichTextComponent
                    text={description}
                    editProps={{ onEdit: this.handleTextChange, requestContext: this.props.context.request }}
                />
            )
        };
    };

    @action
    private readonly init = async (): Promise<void> => {
        const {
            data,
            config: { showCheckbox }
        } = this.props;

        const hasInvoiceLine = this.props.data.checkout.result?.checkoutCart.hasInvoiceLine;

        // If hasInvoiceLine is true, this is for b2b and isTermsAccepted should be true
        if (hasInvoiceLine || data.checkout.result?.isTermsAndConditionAccepted || !showCheckbox) {
            await this.updateTermsAndConditionsAcceptance(true);
            this.isTermsAccepted = true;
        } else if (showCheckbox) {
            await this.updateTermsAndConditionsAcceptance(false);
            this.isTermsAccepted = false;
        }

        this.props.moduleState.init({
            onEdit: this.onEdit,
            onCancel: this.onCancel,
            onSubmit: this.onSubmit,
            status: hasInvoiceLine ? 'disabled' : this.isTermsAccepted ? 'ready' : 'updating'
        });
    };

    private readonly getErrorMessage = (): ICheckoutTermsAndConditionsError => {
        const { errorMessageTitle } = this.props.resources;
        return {
            error: { tag: 'div', className: 'ms-checkout-terms-and-conditions__error', role: 'alert', 'aria-live': 'assertive' },
            title: <ErrorTitleComponent {...{ title: errorMessageTitle }} />,
            message: <ErrorMessageComponent {...{ message: this.errorMessage }} />
        };
    };

    private readonly getCancelButton = (): React.ReactNode => {
        const { cancelBtnLabel = 'Cancel', cancelBtnAriaLabel } = this.props.resources;
        return (
            <Button
                className='ms-checkout-terms-and-conditions__button-cancel'
                title={cancelBtnLabel}
                aria-label={cancelBtnAriaLabel}
                color='secondary'
                onClick={this.onCancel}
            >
                {cancelBtnLabel}
            </Button>
        );
    };

    private readonly getSaveButton = (): React.ReactNode => {
        const { saveBtnLabel = 'Save', saveBtnAriaLabel } = this.props.resources;
        return (
            <Button
                className='ms-checkout-terms-and-conditions__button-save'
                title={saveBtnLabel}
                aria-label={saveBtnAriaLabel}
                color='primary'
                onClick={this.onSubmit}
            >
                {saveBtnLabel}
            </Button>
        );
    };

    private readonly getEditButton = (): React.ReactNode => {
        const { editBtnLabel = 'Change', editBtnAriaLabel } = this.props.resources;
        return (
            <Button
                className='ms-checkout-terms-and-conditions__button-edit'
                title={editBtnLabel}
                aria-label={editBtnAriaLabel}
                color='primary'
                onClick={this.onEdit}
            >
                {editBtnLabel}
            </Button>
        );
    };

    private readonly onEdit = (): void => {
        this.props.telemetry.information('Terms and conditions onEdit is called.');

        this.onUpdating();
    };

    private readonly onSubmit = async (): Promise<void> => {
        this.props.telemetry.information('Terms and conditions onSubmit is called.');

        if (!this.isTermsAccepted) {
            if (this.isAutoSave) {
                await this.updateTermsAndConditionsAcceptance(this.isTermsAccepted);
            } else {
                this.setError(this.props.resources.errorMessage);
                this.props.moduleState.setHasError(true);
            }
        } else {
            this.clearError();
            await this.updateTermsAndConditionsAcceptance(this.isTermsAccepted);
            this.props.moduleState.onReady();
        }
    };

    private readonly onUpdating = () => {
        this.props.moduleState.onUpdating();
    };

    private readonly setError = (errorMessage: string): void => {
        this.props.telemetry.error(errorMessage);
        this.props.moduleState.setHasError(true);
        this.props.moduleState.onUpdating();
        this.errorMessage = errorMessage;
    };

    private readonly onCancel = (): void => {
        this.props.telemetry.information('Terms and conditions onCancel is called.');

        // Reset to the saved value
        if (!this.props.data.checkout.result) {
            return;
        }

        const isTermsAccepted = this.props.data.checkout.result.isTermsAndConditionAccepted;
        if (!isTermsAccepted) {
            // It has no saved email, set to status updating and request user to fill the email form
            this.props.moduleState.onUpdating();
        } else {
            // It has saved email, set to status ready
            this.isTermsAccepted = true;
            this.props.moduleState.setHasError(false);
            this.props.moduleState.onReady();
        }
    };

    private readonly updateTermsAndConditionsAcceptance = async (isTermsAccepted: boolean): Promise<void> => {
        if (this.props.data.checkout.result) {
            await this.props.data.checkout.result.updateTermsAndConditionsAcceptance({ newIsTermsAndConditionAccepted: isTermsAccepted });
        }
    };

    private readonly clearError = (): void => {
        this.props.moduleState.setHasError(false);
        this.errorMessage = undefined;
    };

    private readonly onChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const checked = event.target.checked;
        this.isTermsAccepted = checked;

        // if (this.props.data.checkout.result?.shouldEnableSinglePaymentAuthorizationCheckout) {
        //     await this.props.data.checkout.result.updateIsTermsAndConditionsChecked({ newIsTermsAndConditionChecked: checked });
        // }
        if (this.props.data.checkout.result) {
            await this.props.data.checkout.result.updateIsTermsAndConditionsChecked({ newIsTermsAndConditionChecked: checked });
        }

        // if (this.isAutoSave) {
        //     await this.onSubmit();
        // }
        await this.onSubmit();
    };
}

export default CheckoutTermsAndConditions;
